import { useEffect, useMemo, useState } from 'react';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import { ROAD_NETWORK_FCLASS_GROUPS } from '../../shared/constants/road-network-fclasses';

/**
 * Modal to pick road fclass values for included_fclasses / excluded_fclasses.
 */
export default function RoadFclassPickerDialog({
  open,
  title,
  selectedValues,
  onClose,
  onConfirm,
}) {
  const { _ } = useLingui();
  const [checked, setChecked] = useState(() => new Set());

  useEffect(() => {
    if (!open) return;
    setChecked(new Set(Array.isArray(selectedValues) ? selectedValues : []));
  }, [open, selectedValues]);

  const selectedCount = checked.size;

  const toggle = (value) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const selectAll = () => {
    setChecked(
      new Set(ROAD_NETWORK_FCLASS_GROUPS.flatMap((g) => g.values.map((v) => v.value))),
    );
  };

  const clearAll = () => setChecked(new Set());

  const handleConfirm = () => {
    const list = Array.from(checked).sort();
    onConfirm(list.length === 0 ? null : list);
  };

  const groupLabels = useMemo(
    () => ({
      primary: _(msg`Main roads`),
      urban: _(msg`Minor / urban`),
      paths: _(msg`Unpaved / paths`),
      other: _(msg`Other`),
    }),
    [_],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button size="small" onClick={selectAll}>
            <Trans>Select all</Trans>
          </Button>
          <Button size="small" onClick={clearAll}>
            <Trans>Clear</Trans>
          </Button>
          <Typography variant="body2" sx={{ ml: 'auto', alignSelf: 'center' }}>
            {_(msg`${selectedCount} selected`)}
          </Typography>
        </Box>
        {ROAD_NETWORK_FCLASS_GROUPS.map((group) => (
          <Box key={group.id} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              {groupLabels[group.id] || group.label}
            </Typography>
            <FormGroup>
              {group.values.map((item) => (
                <FormControlLabel
                  key={item.value}
                  control={
                    <Checkbox
                      size="small"
                      checked={checked.has(item.value)}
                      onChange={() => toggle(item.value)}
                    />
                  }
                  label={item.label}
                />
              ))}
            </FormGroup>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          <Trans>Cancel</Trans>
        </Button>
        <Button variant="contained" onClick={handleConfirm}>
          <Trans>Save</Trans>
        </Button>
      </DialogActions>
    </Dialog>
  );
}
